// ============================================================================
// transcriptWords — пословные метки расшифровки речи (SSoT клиента, §2.4):
// разбор ответа сервера (мс, int64 → bigint) и поиск активного слова.
// Чистые функции без React: плеер и панель расшифровки используют их общие.
// ============================================================================

import type { TranscriptWord } from '@zdc/sdk';

/** Слово расшифровки в секундах (внутренний формат панели). */
export interface TranscriptWordData {
  word: string;
  /** начало, секунды */
  start: number;
  /** конец, секунды */
  end: number;
}

/** Пословные метки сервера (мс) → секунды; мусор отбрасывается. */
export function parseTranscriptWords(words: readonly TranscriptWord[]): TranscriptWordData[] {
  const out: TranscriptWordData[] = [];
  for (const w of words) {
    const text = String(w.word || '');
    const start = Number(w.startMs) / 1000;
    const end = Number(w.endMs) / 1000;
    if (!text || !Number.isFinite(start) || start < 0) continue;
    if (!Number.isFinite(end) || end < start) continue;
    out.push({ word: text, start, end });
  }
  let ordered = true;
  for (let i = 1; i < out.length && ordered; i++) {
    if (out[i].start < out[i - 1].start) ordered = false;
  }
  if (!ordered) out.sort((a, b) => a.start - b.start);
  return out;
}

/** Индекс активного интервала (последний начавшийся, чьё время ещё не
 *  кончилось; в промежутках активного нет). Бинарный поиск — общий для
 *  пословных меток и глав-сегментов (SSoT, без дублей). */
export function findActiveIntervalIndex(items: readonly { start: number; end: number }[], currentSec: number): number {
  if (items.length === 0 || !Number.isFinite(currentSec)) return -1;
  let lo = 0;
  let hi = items.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (items[mid].start <= currentSec) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (found < 0 || currentSec >= items[found].end) return -1;
  return found;
}

/** Индекс активного слова (см. findActiveIntervalIndex). */
export function findActiveWordIndex(words: readonly TranscriptWordData[], currentSec: number): number {
  return findActiveIntervalIndex(words, currentSec);
}
